// @flow

import React from 'react'
import styled, { css } from 'styled-components'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import queryString from 'query-string'
import { containerIds, setUIState } from 'redux/modules/ui'
import { setUser } from 'redux/modules/local/user'
import { ownerName } from 'frontend_core/dist/helpers/format'
import {
  helpUrl,
  businessSummaryUrl,
  businessBaseUrl,
  dashboardUrl,
  accountLoginUrl,
  accountLogoutUrl,
  accountSignUpUrl,
} from 'frontend_core/dist/helpers/url'
import Path from 'routes/path'
import AccountMenu from 'components/shared/header/AccountMenu'
import CategorySearchHeader from 'components/organisms/CategorySearchHeader'
import BadgeView from 'components/shared/header/BadgeView'
import { graphql } from 'react-apollo'
import gql from 'graphql-tag'
import { viewerFragment } from 'common/fragments'
import SMConst from 'frontend_core/dist/constants/sm-const'
import ServiceMessages from 'components/shared/header/ServiceMessages'
import ToDoList from 'components/shared/header/ToDoList'
import MobileNotification from 'components/shared/header/MobileNotification'
import NotificationList from 'components/shared/header/NotificationList'
import SearchHeaderContainer from 'containers/organisms/SearchHeader'
import RestClient from 'frontend_core/dist/rest/client'
import { imagePath } from 'frontend_core/dist/helpers/image'
import Colors from 'components/styles/colors'
import { zIndexes } from 'frontend_core/dist/styles/variables'

type PropTypes = {
  data: {
    room: Object,
    viewer: Object,
  },
  ui: Object,
  localUser: Object,
  actions: {
    setUIState: Function,
    setUser: Function,
  },
  location: {
    search: string,
  },
  config: Object,
}
// 現ヘッダーのbreakpointに合わせています
const SiteHeader = styled.div`
  ${props =>
    props.isFixed &&
    css`
      position: fixed;
      width: 100%;
      z-index: ${zIndexes.header};
      @media (min-width: 992px) {
        border-bottom: 1px solid ${Colors.grayE};
      }
    `};
`
const SiteHeaderBase = styled.div`
  ${props =>
    props.isFixed &&
    css`
      height: 48px;
      @media (max-width: 991px) {
        height: 58px;
      }
    `};
`

class HeaderContainer extends React.Component<PropTypes> {
  constructor(props: PropTypes) {
    super(props)

    // urlパスから現在のページのeventTypeを設定
    let eventType = null
    if (props.location) {
      const query = queryString.parse(props.location.search, {
        arrayFormat: 'bracket',
      })
      eventType = query.event_type
    }

    props.actions.setUIState(containerIds.HEADER, {
      isUserMenuOpen: false,
      isNotificationsOpen: false,
      isDrawerOpen: false,
      numberDisplay: SMConst.Display.Loading,
      eventType,
    })

    const viewer = props.data.viewer

    const announcements =
      viewer === null || viewer === undefined || viewer.announcements === null
        ? []
        : viewer.announcements.results
    const todoItems =
      viewer === null || viewer === undefined || viewer.todoItems === null
        ? []
        : viewer.todoItems.results
    const newsNumber =
      viewer === null || viewer === undefined ? null : viewer.newsNumber

    props.actions.setUser(viewer)
    props.actions.setUser({
      notifications: [],
      currentPage: 0,
      canRequest: false,
      announcements,
      todoItems,
      newsNumber,
    })
  }

  // 画面上にオーバーレイ表示しているコンポーネントを非表示に
  hideOverlayComponents(e) {
    e.preventDefault()
    this.props.actions.setUIState(containerIds.HEADER, {
      isUserMenuOpen: false,
      isNotificationsOpen: false,
      isMobileNotificationsOpen: false,
    })
  }

  // ユーザーメニューポップアップを表示する
  showUserMenu(e) {
    e.preventDefault()
    this.props.actions.setUIState(containerIds.HEADER, {
      isUserMenuOpen: true,
    })
  }

  // お知らせポップアップを表示する
  showNotifications(e) {
    e.preventDefault()
    this.props.actions.setUIState(containerIds.HEADER, {
      isNotificationsOpen: true,
    })
    this.requestNotifications()
      .then(data => {})
      .catch(error => {
        console.log(error)
      })
  }

  async requestNotifications() {
    const { localUser, ui, actions } = this.props
    if (!ui.isLimit) {
      const nextPage = localUser.currentPage + 1

      actions.setUIState(containerIds.HEADER, {
        isLoading: true,
      })

      const { data } = await this.props.fetchLocations(nextPage)

      actions.setUIState(containerIds.HEADER, {
        isLoading: false,
        numberDisplay: SMConst.Display.Visible,
        isLimit: data.viewer.notifications.results.length < 20,
      })

      const pageInfo = data.viewer.notifications.pageInfo

      const notifications = [].concat(
        localUser.notifications,
        data.viewer.notifications.results,
      )

      const userState = {
        notifications,
        canRequest: pageInfo.hasNextPage,
        currentPage: nextPage,
      }

      actions.setUser(userState)

      if (localUser.newsNumber > 0) {
        const queries = {}
        if (nextPage !== 1) queries.page = nextPage
        this.checkNotifications(localUser.username, queries)
      }
    }
  }

  tableViewDidScroll(e) {
    observeScrollToBottom(e.currentTarget, () => {
      this.requestNotifications()
        .then(data => {})
        .catch(error => {
          console.log(error)
        })
    })
  }

  showMobileNotifications(e) {
    e.preventDefault()
    this.requestNotifications()
      .then(data => {})
      .catch(error => {
        console.log(error)
      })
    this.closeDrawer(() => {
      this.props.actions.setUIState(containerIds.HEADER, {
        isMobileNotificationsOpen: true,
      })
    })
  }

  closeDrawer(callback) {
    this.refs.closeButton.click()
    setTimeout(() => {
      callback()
    }, 300)
  }

  closeMobileNotifications(e) {
    this.props.actions.setUIState(containerIds.HEADER, {
      isMobileNotificationsOpen: false,
    })
  }

  checkNotifications(username, queries) {
    const path = `1/users/${username}/notifications/check.json`
    const query = Object.assign(queries, { per_page: 20 })

    RestClient.patchJson(path, query)
      .then(data => {})
      .catch(error => {
        console.log(error)
      })
  }

  // ログインユーザー向けヘッダー
  renderLoggedIn(currentUser) {
    const {
      ui,
      localUser,
      location,
      data,
      actions,
      isFixedHeader,
      config,
    } = this.props
    const owners = currentUser.owners.results
    const companies = (currentUser.companies || {}).results || []
    const isCorp = localUser.isCorp

    const mobileNewsListView = (
      <div>
        <p className="site-header__message-menu-close">
          <a onClick={e => this.closeMobileNotifications(e)}>
            <i className="fa fa-times" />
          </a>
        </p>
        <MobileNotification
          {...this.props}
          onScroll={e => this.tableViewDidScroll(e)}
          ref="tableView"
        />
      </div>
    )

    let mobileNotificationsClassName = 'js-mobile-notification'
    if (ui.isMobileNotificationsOpen) {
      mobileNotificationsClassName += ' js-mobile-notification-active'
    }

    return (
      <header>
        <SiteHeader isFixed={isFixedHeader} className="site-header clearfix">
          <p className="fl">
            <a href={'/'} className="site-header__logo">
              {i18n.t('spacemarket')}
            </a>
          </p>
          <SearchHeaderContainer
            data={data}
            showForm={ui.isShowHeaderSearch}
            onClick={() => {
              actions.setUIState(containerIds.HEADER, {
                isShowHeaderSearch: !ui.isShowHeaderSearch,
              })
            }}
          />
          <div>
            <ul className="site-header__nav sm-hide">
              <li>
                <a
                  onClick={e => this.showUserMenu(e)}
                  href="#"
                  className="site-header__nav-user"
                >
                  <img
                    src={currentUser.profileImage}
                    alt={currentUser.name}
                    className="site-header__nav-user-icon"
                  />
                  <p className="site-header__nav-user-name link-black link-borderless">
                    <i className="fa fa-caret-down" />
                  </p>
                </a>
                {ui.isUserMenuOpen ? (
                  <div>
                    <a
                      className="js-all-display"
                      onClick={e => this.hideOverlayComponents(e)}
                    />
                    <AccountMenu
                      name={currentUser.name}
                      points={
                        (currentUser.pointBalance || {}).availableAmount || 0
                      }
                      companies={companies}
                      isCorp={isCorp}
                      config={this.props.config}
                    />
                  </div>
                ) : null}
              </li>
              <li>
                <a
                  onClick={e => this.showNotifications(e)}
                  href="#"
                  className="site-header__nav-message"
                >
                  <i className="fa fa-envelope" />
                  <BadgeView
                    announcements={localUser.announcements}
                    todoItems={localUser.todoItems}
                    newsNumber={localUser.newsNumber}
                    onMobileDrawer={false}
                  />
                </a>
                {ui.isNotificationsOpen ? (
                  <div>
                    <a
                      className="js-all-display"
                      onClick={e => this.hideOverlayComponents(e)}
                    />
                    <div className="baloon baloon-top site-header__nav-baloon site-header__nav-baloon-message">
                      <div
                        onScroll={e => this.tableViewDidScroll(e)}
                        className="baloon-inner"
                      >
                        <ServiceMessages
                          currentUser={data.viewer}
                          announcements={localUser.announcements}
                        />
                        <ToDoList
                          currentUser={data.viewer}
                          todoItems={localUser.todoItems}
                        />
                        <NotificationList
                          config={config}
                          currentUser={data.viewer}
                          notifications={localUser.notifications}
                          display={ui.numberDisplay}
                          isLoading={ui.isLoading}
                          isLimit={ui.isLimit}
                        />
                      </div>
                    </div>
                  </div>
                ) : null}
              </li>
              <li>
                <a
                  href={helpUrl()}
                  className="site-header__nav-help link-black link-borderless"
                >
                  <span className="help__text">
                    {i18n.t('page.header.help')}
                  </span>
                </a>
              </li>
              <li>
                <a href={Path.reservations()} className="link-black">
                  <p className="site-header__nav-reservation">
                    {i18n.t('page.header.reservationList')}
                  </p>
                </a>
              </li>
              <li>
                <div className="site-header__nav-favorite">
                  <a href={Path.favoriteLists()}>
                    <span className="link-black">
                      {i18n.t('page.header.favorite')}
                    </span>
                  </a>
                </div>
              </li>
              <li>
                {owners.length > 0 ? (
                  <a
                    href={dashboardUrl(owners[0], '/dashboard')}
                    className="link-black"
                  >
                    <p className="site-header__nav-owner">
                      {i18n.t('page.header.host')}
                    </p>
                    {owners.find(i => i.hasUnreadReservation === true) && (
                      <i className="fa fa-circle site-header__nav-owner-notification" />
                    )}
                  </a>
                ) : (
                  <a
                    href={'/pages/host_entry'}
                    className="site-header__nav-owner link-black"
                  >
                    {i18n.t('page.header.entrySpace')}
                  </a>
                )}
              </li>
            </ul>
          </div>
          {!!location &&
            location.pathname !== '/' && (
              <CategorySearchHeader
                eventType={ui.eventType}
                onChangeOtherEventType={eventType => {
                  window.location = `${Path.search()}?event_type=${eventType}&with_recommend=true`
                }}
              />
            )}
          <div>
            <div className="site-header__smp">
              <input
                type="checkbox"
                className="site-header__drawer-check"
                id="site-header__drawer"
              />
              <label
                className="site-header__drawer-menu-btn"
                htmlFor="site-header__drawer"
              >
                <i className="fa fa-bars" />
              </label>
              <label
                ref="closeButton"
                className="site-header__drawer-close-menu"
                htmlFor="site-header__drawer"
              />
              <nav className="site-header__drawer-menu">
                <ul>
                  <li>
                    <div className="site-header__drawer-name-box">
                      <img
                        src={currentUser.profileImage}
                        alt={currentUser.name}
                        className="site-header__drawer-user-icon"
                      />
                      <p className="site-header__drawer-name-title">
                        {i18n.t('page.header.guest')}
                      </p>
                      <p className="site-header__drawer-user-name">
                        {currentUser.name}
                      </p>
                      <p className="site-header__drawer-user-point">
                        {currentUser.pointBalance == null
                          ? null
                          : (currentUser.pointBalance || {}).availableAmount ||
                            0}
                        <span className="site-header__drawer-user-point-unit">
                          ポイント
                        </span>
                      </p>
                    </div>
                    <ul>
                      <li className="site-header__drawer-menu-list">
                        <a
                          onClick={e => this.showMobileNotifications(e)}
                          href="#"
                          className="site-header__nav-message"
                        >
                          <i className="fa fa-envelope" />
                          {i18n.t('page.header.notification')}
                          <BadgeView
                            announcements={localUser.announcements}
                            todoItems={localUser.todoItems}
                            newsNumber={localUser.newsNumber}
                            onMobileDrawer
                          />
                        </a>
                      </li>
                      <li className="site-header__drawer-menu-list">
                        <a href={Path.favoriteLists()}>
                          <i className="fa fa-heart" />
                          {i18n.t('page.header.favorite')}
                        </a>
                      </li>
                      <li className="site-header__drawer-menu-list">
                        <a href={'/dashboard/reservations'}>
                          <i className="fa fa-file-text-o" />
                          {i18n.t('page.header.reservationList')}
                        </a>
                      </li>
                      <li className="site-header__drawer-menu-list">
                        <a href={'/dashboard/points/available_point_histories'}>
                          <i className="icon icon-spm-point" />
                          {i18n.t('page.header.point')}
                        </a>
                      </li>
                      <li className="site-header__drawer-menu-list">
                        <a href={'/dashboard/settings/account'}>
                          <i className="fa fa-cog" />
                          {i18n.t('page.shared.l_00029')}
                        </a>
                      </li>
                      <li className="site-header__drawer-menu-list">
                        <a href="https://help.spacemarket.com/hc">
                          <i className="fa fa-question" />
                          {i18n.t('page.header.help')}
                        </a>
                      </li>
                      <li className="site-header__drawer-menu-list">
                        <a href={'/referrals'}>
                          <i className="fa fa-users" />
                          {i18n.t('page.header.inviteFriends')}
                        </a>
                      </li>
                      <li className="site-header__drawer-menu-list">
                        <a
                          href="https://beyond.spacemarket.com/know-how/enterprise-use/?utm_source=sp_drawer_menu&utm_medium=referral&utm_campaign=business_lp"
                          target="_blank"
                        >
                          <i className="fa fa-building" />
                          {i18n.t('page.header.aboutBusiness')}
                        </a>
                      </li>
                      <li className="site-header__drawer-menu-list">
                        <a href={accountLogoutUrl(this.props.config)}>
                          <i className="fa fa-sign-out" />
                          {i18n.t('page.shared.l_00069')}
                        </a>
                      </li>
                      {owners.length === 1 && (
                        <li className="site-header__drawer-menu-list site-header__drawer-menu-list-border">
                          <a href={dashboardUrl(owners[0], '/dashboard')}>
                            <i className="icon icon-spm-host" />
                            <span className="site-header__drawer-menu-list-host">
                              {i18n.t('page.header.host')}
                            </span>
                            {owners.find(
                              i => i.hasUnreadReservation === true,
                            ) && (
                              <i className="fa fa-circle site-header__drawer-menu-list-notification" />
                            )}
                          </a>
                        </li>
                      )}
                    </ul>
                  </li>
                  {(() => {
                    if (owners.length >= 2) {
                      const ownerList = owners.map((owner, i) => (
                        <li
                          key={`owner_${owner.id}_${i}`}
                          className="site-header__drawer-menu-list"
                        >
                          <a href={dashboardUrl(owner, '/dashboard')}>
                            <span className="site-header__drawer-menu-list-host">
                              {ownerName(owner)}
                            </span>
                            {owner.hasUnreadReservation === true && (
                              <i className="fa fa-circle site-header__drawer-menu-list-notification" />
                            )}
                          </a>
                        </li>
                      ))
                      return (
                        <li>
                          <p className="site-header__drawer-menu-title">
                            {i18n.t('page.header.host')}
                          </p>
                          <ul>{ownerList}</ul>
                        </li>
                      )
                    }
                    return null
                  })()}
                  {companies.length > 0 ? (
                    <h2 className="site-header__drawer-menu-title">
                      法人アカウント
                    </h2>
                  ) : null}
                  <ul>
                    {companies.map((company, i) => (
                      <li
                        key={`company_${company.id}_${i}`}
                        className="site-header__drawer-menu-list"
                      >
                        <a href={businessSummaryUrl(company.username)}>
                          {company.name}
                        </a>
                      </li>
                    ))}
                    {isCorp ? (
                      <li
                        key="new-business-account"
                        className="site-header__drawer-menu-list"
                      >
                        <a href={businessBaseUrl()}>法人アカウントを作成</a>
                      </li>
                    ) : null}
                  </ul>
                </ul>
              </nav>
            </div>
          </div>
        </SiteHeader>
        <SiteHeaderBase isFixed={isFixedHeader} />
        <div className={mobileNotificationsClassName}>{mobileNewsListView}</div>
      </header>
    )
  }

  // 未ログインユーザー向けヘッダー
  renderAnonymous() {
    const { ui, data, location, actions } = this.props

    return (
      <header>
        <div className="site-header clearfix">
          <p className="fl">
            <a href={Path.home()} className="site-header__logo">
              {i18n.t('spacemarket')}
            </a>
          </p>
          <SearchHeaderContainer
            data={data}
            showForm={ui.isShowHeaderSearch}
            onClick={() => {
              actions.setUIState(containerIds.HEADER, {
                isShowHeaderSearch: !ui.isShowHeaderSearch,
              })
            }}
          />
          <div>
            <ul className="site-header__nav sm-hide">
              <li>
                <a
                  href={accountSignUpUrl(this.props.config)}
                  className="site-header__nav-user"
                >
                  <p className="site-header__nav-user-name link-black">
                    {i18n.t('page.header.registration')}
                  </p>
                </a>
              </li>
              <li>
                <a
                  href={accountLoginUrl(this.props.config)}
                  className="site-header__nav-user"
                >
                  <p className="site-header__nav-user-name link-black">
                    {i18n.t('page.header.login')}
                  </p>
                </a>
              </li>
              <li>
                <a
                  href={helpUrl()}
                  className="site-header__nav-help link-black link-borderless"
                >
                  <span className="help__text">
                    {i18n.t('page.header.help')}
                  </span>
                </a>
              </li>
              <li>
                <a
                  href="/pages/host_entry"
                  className="site-header__nav-owner link-black"
                >
                  {i18n.t('page.header.entrySpace')}
                </a>
              </li>
            </ul>
          </div>
          {!!location &&
            location.pathname !== '/' && (
              <CategorySearchHeader
                eventType={ui.eventType}
                onChangeOtherEventType={eventType => {
                  window.location = `${Path.search()}?event_type=${eventType}&with_recommend=true`
                }}
              />
            )}
          <div>
            <div className="site-header__smp">
              <input
                type="checkbox"
                className="site-header__drawer-check"
                id="site-header__drawer"
              />
              <label
                className="site-header__drawer-menu-btn"
                htmlFor="site-header__drawer"
              >
                <i className="fa fa-bars" />
              </label>
              <label
                className="site-header__drawer-close-menu"
                htmlFor="site-header__drawer"
              />
              <nav className="site-header__drawer-menu">
                <ul>
                  <li>
                    <div className="site-header__drawer-name-box">
                      <img
                        src={imagePath('www/common/user_default_white.png')}
                        alt={i18n.t('page.header.guest')}
                        className="site-header__drawer-user-icon"
                      />
                      <p className="site-header__drawer-name-title">
                        {i18n.t('page.header.guest')}
                      </p>
                      <p className="site-header__drawer-user-name">
                        {i18n.t('page.header.notLogin')}
                      </p>
                    </div>
                    <ul>
                      <li className="site-header__drawer-menu-list">
                        <a href={Path.search()}>
                          <i className="fa fa-search" />
                          {i18n.t('page.header.searchSpace')}
                        </a>
                      </li>
                      <li className="site-header__drawer-menu-list">
                        <a href={'/about'}>
                          <i className="fa fa-info-circle" />
                          {i18n.t('page.header.userGuide')}
                        </a>
                      </li>
                      <li className="site-header__drawer-menu-list">
                        <a href={helpUrl()}>
                          <i className="fa fa-question" />
                          {i18n.t('page.header.help')}
                        </a>
                      </li>
                      <li className="site-header__drawer-menu-list">
                        <a href={accountLoginUrl(this.props.config)}>
                          <i className="fa fa-sign-in" />
                          {i18n.t('page.header.login')}
                        </a>
                      </li>
                      <li className="site-header__drawer-menu-list">
                        <a href={accountSignUpUrl(this.props.config)}>
                          <i className="fa fa-user" />
                          {i18n.t('page.header.registration')}
                        </a>
                      </li>
                      <li className="site-header__drawer-menu-list">
                        <a href={`/pages/host_entry`}>
                          <i className="fa fa-pencil-square-o" />
                          {i18n.t('page.header.entrySpace')}
                        </a>
                      </li>
                      <li className="site-header__drawer-menu-list">
                        <a
                          href="https://beyond.spacemarket.com/know-how/enterprise-use/?utm_source=sp_drawer_menu&utm_medium=referral&utm_campaign=business_lp"
                          target="_blank"
                        >
                          <i className="fa fa-building" />
                          {i18n.t('page.header.aboutBusiness')}
                        </a>
                      </li>
                    </ul>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </header>
    )
  }

  render() {
    const { data } = this.props
    const currentUser = this.props.currentUser || data.viewer

    if (currentUser) {
      return this.renderLoggedIn(currentUser)
    }
    return this.renderAnonymous()
  }
}

function observeScrollToBottom(target, callback) {
  const y = target.scrollTop + target.clientHeight
  if (target.scrollHeight <= y) callback()
}

function mapStateToProps(state) {
  return state
}

function mapDispatchToProps(dispatch) {
  const bindActions = { setUIState, setUser }
  return { actions: bindActionCreators(bindActions, dispatch) }
}

const withData = graphql(
  gql`
  query HeaderContainer {
    viewer {
      ${viewerFragment},
    }
  }
`,
  {
    props({ data: { loading, page, fetchMore } }) {
      return {
        loading,
        page,
        fetchLocations: requestPage => {
          return fetchMore({
            query: gql`
              query HeaderContainer($requestPage: Int) {
                viewer {
                  notifications(page: $requestPage, perPage: 20, filter: ALL) {
                    results {
                      id
                      content
                      link
                      fromType
                      readAt
                      createdAt
                      notificationType
                      aboutType
                      about {
                        ... on Reservation {
                          id
                          uid
                          user {
                            id
                          }
                          owner {
                            username
                          }
                        }
                        ... on Owner {
                          username
                        }
                      }
                      from {
                        ... on User {
                          id
                          contactName
                          profileImage
                          deletedAt
                          createdAt
                        }
                        ... on Owner {
                          id
                          ownerName
                          profileImage
                          deletedAt
                          createdAt
                        }
                        ... on AdminUser {
                          id
                          deletedAt
                          createdAt
                        }
                        ... on ServiceAdmin {
                          id
                          createdAt
                        }
                      }
                    }
                    pageInfo {
                      nextPage
                      hasNextPage
                    }
                  }
                }
              }
            `,
            variables: {
              page: requestPage,
            },
            updateQuery: (prev, { fetchMoreResult }) => {
              if (!fetchMoreResult.data) {
                return prev
              }
              return {
                data: [...prev, ...fetchMoreResult.data],
              }
            },
          })
        },
      }
    },
  },
)(HeaderContainer)

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withData)



// WEBPACK FOOTER //
// ./src/containers/organisms/HeaderContainer.jsx